import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import * as Icon from "react-feather";
import { H5, P } from "../Texts";
import moment from "moment";
import { Badge, LinkButton } from "../Buttons";
import { Link, useParams } from "react-router-dom";
import pillStylesEnum from "../../../shared/fixtures/pillStylesEnum";
import DeleteReferralFormModal from "../../organisms/DeleteReferralFormModal.js";

const ReferralFormCardStyle = styled.div`
  border-radius: 4px;
`;

const DropdownStyle = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
  border-radius: 4px;
  z-index: 50;
  min-width: 200px;
`;

const DropDown = ({
  orgId,
  toggleDropDown,
  formId,
  url,
  toggleDeleteFormModal,
  setSelectedFormURL,
  setModalStates,
  modalStates,
}) => {
  const formIdentifier = url.split("/").pop();

  const handleAction = (action) => (e) => {
    e.preventDefault();
    e.stopPropagation();

    switch (action) {
      case "share":
        setSelectedFormURL(url);
        setModalStates({ ...modalStates, shareLink: true });
        break;
      case "embed":
        setSelectedFormURL(url);
        setModalStates({ ...modalStates, copyCode: true });
        break;
      case "deactivate":
        toggleDeleteFormModal();
        break;
      default:
        break;
    }
    toggleDropDown();
  };

  return (
    <DropdownStyle>
      <Link
        to={`/referral-form/${formIdentifier}`}
        target="_blank"
        rel="noreferrer noopener"
      >
        <LinkButton
          color="#716E8B"
          className="whitespace-nowrap mb-2 text-left hover:bg-blue-100 w-full"
          p="4px 40px 4px 20px"
        >
          Preview
        </LinkButton>
      </Link>

      <Link to={`/dashboard/organization/${orgId}/referral-forms/edit/${formId}`}>
        <LinkButton
          color="#716E8B"
          className="whitespace-nowrap mb-2 text-left hover:bg-blue-100 w-full"
          p="4px 40px 4px 20px"
        >
          Edit
        </LinkButton>
      </Link>

      <LinkButton
        color="#716E8B"
        className="whitespace-nowrap mb-2 text-left hover:bg-blue-100 w-full"
        p="4px 40px 4px 20px"
        onClick={handleAction("share")}
      >
        Share url
      </LinkButton>

      <LinkButton
        color="#716E8B"
        className="whitespace-nowrap mb-2 text-left hover:bg-blue-100 w-full"
        p="4px 40px 4px 20px"
        onClick={handleAction("embed")}
      >
        Embed form
      </LinkButton>

      <LinkButton
        color="#716E8B"
        className="whitespace-nowrap text-left hover:bg-blue-100 w-full"
        p="4px 40px 4px 20px"
        onClick={handleAction("deactivate")}
      >
        Deactivate
      </LinkButton>
    </DropdownStyle>
  );
};

export const ReferralFormCard = ({
  form: { service, url, updatedAt, status, numberOfSubmissions, id: formId },
  setSelectedFormURL,
  setModalStates,
  modalStates,
  width,
}) => {
  const { id: orgId } = useParams();
  const [showFormDropDown, setShowFormDropDown] = useState(false);
  const [showDeleteFormModal, setShowDeleteFormModal] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  const toggleDeleteFormModal = () => setShowDeleteFormModal(!showDeleteFormModal);

  const toggleFormActionDropDown = (e) => {
    e.stopPropagation();
    setShowFormDropDown(!showFormDropDown);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside both the menu and the toggle button
      if (
        showFormDropDown &&
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setShowFormDropDown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showFormDropDown]);

  return (
    <ReferralFormCardStyle width={width} className="bg-white p-5 shadow relative">
      <div className="flex justify-between">
        <H5
          color="#22202C"
          fontFamily="regular"
          fontSize="16px"
          smallSize="16px"
          className="mb-0 font-semibold line-clamp-2 flex-grow"
        >
          {service?.name}
        </H5>
        <div className="relative" ref={menuRef}>
          <div ref={buttonRef}>
            <Icon.MoreVertical
              color="#25282B"
              size={18}
              className="flex-shrink-0 cursor-pointer"
              onClick={toggleFormActionDropDown}
            />
          </div>
          {showFormDropDown && (
            <DropDown
              orgId={orgId}
              formId={formId}
              url={url}
              modalStates={modalStates}
              setModalStates={setModalStates}
              setSelectedFormURL={setSelectedFormURL}
              toggleDropDown={() => setShowFormDropDown(false)}
              toggleDeleteFormModal={toggleDeleteFormModal}
            />
          )}
        </div>
      </div>

      <P color="#C4C4C4" fontSize="14px" smallSize="14px" className="mb-20 truncate">
        Last updated on {moment(updatedAt ?? new Date()).format("ll")}
      </P>

      <div className="flex justify-between items-center">
        <P
          color="#000000"
          fontSize="14px"
          smallSize="14px"
          className="my-0 truncate"
        >
          {numberOfSubmissions} referral(s) received
        </P>
        <Badge
          as="div"
          borderRadius="4px"
          color={pillStylesEnum[status]?.color}
          bg={pillStylesEnum[status]?.bg}
          p="3px 8px"
          fontSize="12px"
          fontFamily="semi"
          className="capitalize"
        >
          {pillStylesEnum[status]?.title}
        </Badge>
      </div>

      {showDeleteFormModal && (
        <DeleteReferralFormModal
          closeModal={toggleDeleteFormModal}
          formId={formId}
        />
      )}
    </ReferralFormCardStyle>
  );
};
